import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Divider,
  Alert,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  DollarOutlined,
  BankOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Payment {
  id: string;
  paymentNumber: string;
  type: 'inbound' | 'outbound';
  customerName: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  status: 'draft' | 'posted' | 'reconciled' | 'cancelled';
  reference?: string;
}

const PaymentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const samplePayments: Payment[] = [
    {
      id: '1',
      paymentNumber: 'PAY-001',
      type: 'inbound',
      customerName: 'Acme Corp',
      amount: 3000,
      paymentDate: dayjs().format('YYYY-MM-DD'),
      paymentMethod: 'Bank Transfer',
      status: 'posted',
      reference: 'INV-001'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'posted': return 'blue';
      case 'reconciled': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const paymentColumns: ColumnsType<Payment> = [
    {
      title: 'Payment #',
      dataIndex: 'paymentNumber',
      key: 'paymentNumber'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'inbound' ? 'green' : 'orange'}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Customer/Supplier',
      dataIndex: 'customerName',
      key: 'customerName'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate'
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} />
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} />
        </Space>
      )
    }
  ];

  const renderPaymentsTab = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Payments"
              value={samplePayments.length}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inbound Payments"
              value={samplePayments.filter(p => p.type === 'inbound').reduce((sum, p) => sum + p.amount, 0)}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Outbound Payments"
              value={samplePayments.filter(p => p.type === 'outbound').reduce((sum, p) => sum + p.amount, 0)}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Reconciled"
              value={samplePayments.filter(p => p.status === 'reconciled').length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Payments"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
            New Payment
          </Button>
        }
      >
        <Table
          columns={paymentColumns}
          dataSource={samplePayments}
          rowKey="id"
          loading={loading}
        />
      </Card>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Payment Management</Title>
        <Text type="secondary">
          Manage payments, payment methods, and reconciliation
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Payments" key="payments">
          {renderPaymentsTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Payment Methods" key="methods">
          <Card title="Payment Methods">
            <Button type="primary" icon={<PlusOutlined />}>
              New Payment Method
            </Button>
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Reconciliation" key="reconciliation">
          <Card title="Payment Reconciliation">
            {/* Reconciliation content */}
          </Card>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title="Create Payment"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentNumber" label="Payment Number">
                <Input placeholder="PAY-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="Payment Type">
                <Select placeholder="Select type">
                  <Option value="inbound">Inbound</Option>
                  <Option value="outbound">Outbound</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="Amount">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentDate" label="Payment Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="paymentMethod" label="Payment Method">
                <Select placeholder="Select payment method">
                  <Option value="bank">Bank Transfer</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="check">Check</Option>
                  <Option value="card">Credit Card</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentManagement;
